import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { format, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    BarChart3, CalendarIcon, FileJson, Loader2,
    RefreshCw, Settings, ShoppingBag, Trash2, MapPin,
} from 'lucide-react';
import {
    Bar, BarChart, CartesianGrid, ComposedChart, Line, LineChart,
    ResponsiveContainer, XAxis, YAxis,
} from 'recharts';

import { useSalesReport } from '../../hooks/useSalesReport';
import { useToast } from '../../hooks/useToast';
import { CommissionService } from '../../services/commission.service';
import { FunctionsService } from '../../services/functions.service';
import { cn, formatCurrency } from '../../lib/utils';

import { Button } from '@/components/ui/button';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
registerLocale('es', es);
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle, DialogTrigger, DialogClose,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import DashboardHeader from './DashboardHeader';
import DatePresetButtons, { getPresetRange, DEFAULT_PRESET } from '../common/DatePresetButtons';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DEFAULT_RANGE = getPresetRange(DEFAULT_PRESET);

const normalizeKey = (value) => value.trim().toLowerCase().replace(/\./g, '');

const parseMoney = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number(value.replace(/\s/g, '').replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
};

// Use subtotal (netSales) — pre-tax amount
const getOrderNetSales = (order) => {
    const candidates = [order?.netSales, order?.subtotal, order?.totalNetSales];
    for (const c of candidates) {
        const v = parseMoney(c);
        if (v > 0) return v;
    }
    return 0;
};

const normalizeCommissionSettingsMap = (settings) => {
    const out = {};
    Object.entries(settings ?? {}).forEach(([key, value]) => {
        const displayName = value?.displayName ?? key;
        out[normalizeKey(displayName)] = {
            displayName,
            enabled:    Boolean(value?.enabled),
            percentage: Number.isFinite(Number(value?.percentage)) ? Number(value?.percentage) : 0,
        };
    });
    return out;
};

const SUMMARY_CHART_CONFIG = {
    netSales: { label: 'Ventas netas', color: 'hsl(142 70% 45%)' },
    orders:   { label: 'Órdenes',      color: 'hsl(208 80% 65%)' },
};
const SELLER_CHART_CONFIG = {
    netSales:   { label: 'Ventas netas', color: 'hsl(199 82% 50%)' },
    orders:     { label: 'Órdenes',      color: 'hsl(27 96% 61%)'  },
    commission: { label: 'Comisiones',   color: 'hsl(48 94% 58%)'  },
};
const PRODUCT_CHART_CONFIG = {
    totalSales: { label: 'Ventas totales', color: 'hsl(262 83% 62%)' },
    units:      { label: 'Unidades',       color: 'hsl(13 88% 63%)'  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export function DashboardSummary() {
    const { toast } = useToast();
    const { user } = useAuth();
    const { report, loading, error, getSalesReport, clearReport } = useSalesReport();

    const [dateRange,       setDateRange]       = useState(DEFAULT_RANGE);
    const [selectedPreset,  setSelectedPreset]  = useState(DEFAULT_PRESET);
    const [selectedBranch,  setSelectedBranch]  = useState('ALL');
    const [commissionConfig, setCommissionConfig] = useState({});
    const [productImages,   setProductImages]   = useState({});
    const [productSearch,   setProductSearch]   = useState('');
    const [productSort,     setProductSort]     = useState({ key: 'totalSales', direction: 'desc' });
    const [isSummaryChartOpen,  setIsSummaryChartOpen]  = useState(false);
    const [isSellerChartOpen,   setIsSellerChartOpen]   = useState(false);
    const [isProductChartOpen,  setIsProductChartOpen]  = useState(false);
    const [isLogOpen,           setIsLogOpen]           = useState(false);

    // ── Date helpers ──────────────────────────────────────────────────────────

    const formatRangeLabel = useCallback((range) => {
        if (!range.from || !range.to) return 'Selecciona un rango';
        const sameDay = range.from.toDateString() === range.to.toDateString();
        return sameDay
            ? format(range.from, "EEEE d 'de' MMMM yyyy", { locale: es })
            : `${format(range.from, "d 'de' MMMM", { locale: es })} - ${format(range.to, "d 'de' MMMM yyyy", { locale: es })}`;
    }, []);

    // ── Report loading ────────────────────────────────────────────────────────

    const runReport = useCallback(async (range) => {
        const r = range ?? dateRange;
        if (!r.from || !r.to) return;
        setSelectedBranch('ALL'); // Reset branch on new report
        const result = await getSalesReport(r.from, r.to);
        if (!result) {
            toast({ title: 'Sin datos', description: 'No se pudieron obtener ventas para el período.', variant: 'destructive' });
        }
    }, [dateRange, getSalesReport, toast]);

    useEffect(() => { runReport(); }, []); // eslint-disable-line

    // Load commission settings
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        CommissionService.getSettings()
            .then((stored) => {
                if (cancelled || !stored) return;
                setCommissionConfig(prev => ({ ...prev, ...normalizeCommissionSettingsMap(stored) }));
            })
            .catch(console.error);
        return () => { cancelled = true; };
    }, [user]);

    // Auto-initialize commission config from sellers
    useEffect(() => {
        if (!report?.sellers?.length) return;
        setCommissionConfig(prev => {
            const next = { ...prev };
            let changed = false;
            report.sellers.forEach(({ sellerName }) => {
                const key = normalizeKey(sellerName);
                if (!next[key]) {
                    next[key] = { displayName: sellerName, enabled: true, percentage: 1 };
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [report?.sellers]);

    const handlePresetChange = useCallback((preset, nextRange) => {
        setSelectedPreset(preset);
        setDateRange(nextRange);
        runReport(nextRange);
    }, [runReport]);

    const handleClearCache = useCallback(async () => {
        try {
            clearReport();
            await FunctionsService.clearCache();
            toast({ title: 'Caché limpiado', description: 'La próxima generación traerá datos actualizados.' });
            runReport();
        } catch (err) {
            toast({ title: 'Error', description: err.message ?? 'No se pudo limpiar la caché.', variant: 'destructive' });
        }
    }, [clearReport, runReport, toast]);

    // ── Branch filtering ──────────────────────────────────────────────────────

    // All branches detected from orders
    const branches = useMemo(() => {
        if (!report?.orders?.length) return [];
        const set = new Set();
        report.orders.forEach(o => {
            const b = o.branch;
            if (b && !b.includes('ALMACEN')) set.add(b);
        });
        return Array.from(set).sort();
    }, [report?.orders]);

    // Orders filtered by the selected branch
    const filteredOrders = useMemo(() => {
        if (!report?.orders?.length) return [];
        if (selectedBranch === 'ALL') return report.orders;
        return report.orders.filter(o => (o.branch || '') === selectedBranch);
    }, [report?.orders, selectedBranch]);

    // ── Derived metrics (all from filteredOrders) ─────────────────────────────

    const totalNetSales = useMemo(
        () => filteredOrders.reduce((s, o) => s + getOrderNetSales(o), 0),
        [filteredOrders]
    );
    const totalOrders = filteredOrders.length;

    const totalTaxes = useMemo(
        () => filteredOrders.reduce((s, o) => s + parseMoney(o.tax ?? 0), 0),
        [filteredOrders]
    );

    // Sellers derived from filtered orders — respects per-branch view
    const sellerRows = useMemo(() => {
        const map = new Map();
        filteredOrders.forEach(order => {
            const name   = order.sellerName || order.seller || 'Sin Asignar';
            const sales  = getOrderNetSales(order);
            const cur    = map.get(name) || { sales: 0, orders: 0 };
            map.set(name, { sales: cur.sales + sales, orders: cur.orders + 1 });
        });

        return Array.from(map.entries())
            .map(([name, data]) => {
                const key    = normalizeKey(name);
                const config = commissionConfig[key];
                const pct    = config?.enabled ? (config.percentage ?? 1) : 1;
                const commission = data.sales * (pct / 100);
                return { key, name, netSales: data.sales, orders: data.orders, commission };
            })
            .sort((a, b) => b.netSales - a.netSales);
    }, [filteredOrders, commissionConfig]);

    const totalCommissions = useMemo(
        () => sellerRows.reduce((s, r) => s + r.commission, 0),
        [sellerRows]
    );

    const bestSeller = useMemo(() => sellerRows[0]?.name ?? 'Sin registros', [sellerRows]);

    // Products derived from filtered orders
    const productRows = useMemo(() => {
        const aggregate = new Map();
        filteredOrders.forEach(order => {
            const items = order.items ?? order.products ?? [];
            items.forEach(item => {
                const name      = item.name ?? item.productName ?? 'Producto sin nombre';
                const key       = name.toLowerCase();
                const quantity  = Number.isFinite(Number(item.quantity)) ? Number(item.quantity) : 0;
                const totalSales = parseMoney(item.totalSales) > 0
                    ? parseMoney(item.totalSales)
                    : parseMoney(item.price) * quantity;
                const productId = item.id != null ? String(item.id) : undefined;
                const imageUrl  = item.imageUrl ?? undefined;

                const cur = aggregate.get(key) ?? { name, units: 0, sales: 0, imageUrl, productIds: new Set() };
                aggregate.set(key, {
                    name,
                    units:      cur.units + quantity,
                    sales:      cur.sales + totalSales,
                    imageUrl:   cur.imageUrl ?? imageUrl,
                    productIds: productId ? new Set(cur.productIds).add(productId) : cur.productIds,
                });
            });
        });

        return Array.from(aggregate.entries())
            .map(([key, v]) => ({
                key,
                name:       v.name,
                units:      v.units,
                totalSales: v.sales,
                imageUrl:   v.imageUrl,
                productIds: Array.from(v.productIds),
            }))
            .sort((a, b) => b.totalSales - a.totalSales);
    }, [filteredOrders]);

    // Chart data from filtered orders
    const summaryChartData = useMemo(() => {
        const buckets = new Map();
        filteredOrders.forEach(order => {
            const dateValue = order?.date ?? order?.orderDate ?? '';
            if (!dateValue) return;
            const dayKey = typeof dateValue === 'string' ? dateValue.substring(0, 10) : format(new Date(dateValue), 'yyyy-MM-dd');
            const bucket = buckets.get(dayKey) ?? { label: dayKey.substring(5), netSales: 0, orders: 0 };
            buckets.set(dayKey, {
                label:    bucket.label,
                netSales: bucket.netSales + getOrderNetSales(order),
                orders:   bucket.orders + 1,
            });
        });
        return Array.from(buckets.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([, v]) => v);
    }, [filteredOrders]);

    const sellerChartData = useMemo(
        () => sellerRows.map(s => ({ name: s.name.split(' ')[0], netSales: s.netSales, orders: s.orders, commission: s.commission })),
        [sellerRows]
    );

    const productChartData = useMemo(
        () => [...productRows].sort((a, b) => b.units - a.units).slice(0, 12)
              .map(p => ({ name: p.name, totalSales: p.totalSales, units: p.units })),
        [productRows]
    );

    // ── Product image loading ─────────────────────────────────────────────────

    useEffect(() => {
        const pending = new Set();
        productRows.forEach(row => {
            row.productIds.forEach(id => { if (id?.trim() && !productImages[id.trim()]) pending.add(id.trim()); });
        });
        if (!pending.size) return;

        let cancelled = false;
        FunctionsService.getProductsByIds(Array.from(pending))
            .then(res => {
                if (cancelled || !res.data) return;
                setProductImages(prev => ({ ...prev, ...Object.fromEntries(Object.entries(res.data).map(([id, info]) => [id, info ?? {}])) }));
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [productRows]); // eslint-disable-line

    // ── Product display with search/sort ──────────────────────────────────────

    const handleSort = (key) => setProductSort(prev => ({
        key,
        direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));

    const displayProductRows = useMemo(() => {
        let rows = productRows.map(row => ({
            ...row,
            resolvedImageUrl: row.imageUrl ?? row.productIds.map(id => productImages[id]?.imageUrl).find(v => typeof v === 'string' && v.length > 0) ?? null,
        }));
        if (productSearch.trim()) {
            const q = productSearch.toLowerCase();
            rows = rows.filter(r => r.name.toLowerCase().includes(q));
        }
        return rows.sort((a, b) => {
            const va = a[productSort.key], vb = b[productSort.key];
            return productSort.direction === 'asc' ? (va < vb ? -1 : va > vb ? 1 : 0) : (vb < va ? -1 : vb > va ? 1 : 0);
        });
    }, [productRows, productImages, productSearch, productSort]);

    // ── Commission save ───────────────────────────────────────────────────────

    const handleCommissionSave = useCallback(async (next) => {
        const normalized = normalizeCommissionSettingsMap(next);
        try {
            await CommissionService.saveSettings(normalized);
            setCommissionConfig(normalized);
            toast({ title: 'Comisiones guardadas', description: 'Configuración actualizada correctamente.' });
        } catch (err) {
            toast({ title: 'Error al guardar comisiones', description: err?.message, variant: 'destructive' });
            throw err;
        }
    }, [toast]);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <section className="space-y-4 md:space-y-6 px-4 py-5 sm:p-6 bg-gray-950 min-h-screen text-white">
            <DashboardHeader activeTab="dashboard" />

            {/* Controls */}
            <Card className="border-white/10 bg-white/5 text-white">
                <CardHeader className="space-y-3 md:space-y-4 p-4 sm:p-6">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <CardTitle className="text-xl font-semibold">Resumen de Ventas</CardTitle>
                            <CardDescription className="text-white/60">
                                {selectedBranch === 'ALL' ? 'Todas las sucursales' : selectedBranch}
                                {report?.orders?.length ? ` · ${report.orders.length} órdenes totales` : ''}
                            </CardDescription>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <Button variant="outline" size="sm" title="Ver gráficas" onClick={() => setIsSummaryChartOpen(true)}>
                                <BarChart3 className="h-4 w-4" /><span className="hidden sm:inline">Gráficas</span>
                            </Button>
                            <Button variant="outline" size="sm" title="Limpiar caché" className="bg-red-500/15 text-red-200 hover:bg-red-500/25 border-red-500/20" onClick={handleClearCache} disabled={loading}>
                                <Trash2 className="h-4 w-4" /><span className="hidden lg:inline">Limpiar caché</span>
                            </Button>
                            <Button variant="outline" size="sm" title="Debug" onClick={() => setIsLogOpen(true)}>
                                <FileJson className="h-4 w-4" /><span className="hidden lg:inline">Debug</span>
                            </Button>
                        </div>
                    </div>

                    {/* Presets + Date */}
                    <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3">
                        <DatePresetButtons value={selectedPreset} onChange={handlePresetChange} disabled={loading} />
                        <div className="flex items-center gap-2 lg:ml-auto">

                        <Dialog>
                            <DialogTrigger asChild>
                                <Button variant="outline" className="min-w-0 flex-1 lg:flex-none justify-start">
                                    <CalendarIcon className="h-4 w-4" />
                                    <span className="truncate">{formatRangeLabel(dateRange)}</span>
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="w-auto p-6 bg-slate-950 border-white/10 text-white sm:max-w-xl">
                                <DialogHeader>
                                    <DialogTitle className="text-white mb-4 text-center">Seleccionar Período</DialogTitle>
                                </DialogHeader>
                                <div className="flex flex-col items-center rounded-lg border border-white/5 bg-black/30 p-4">
                                    <style>{`
                                        .react-datepicker{background:#0f172a;border-color:#334155;color:#f8fafc;font-family:inherit}
                                        .react-datepicker__header{background:#1e293b;border-color:#334155}
                                        .react-datepicker__current-month,.react-datepicker__day-name{color:#94a3b8}
                                        .react-datepicker__day{color:#f8fafc}
                                        .react-datepicker__day:hover{background:#334155}
                                        .react-datepicker__day--selected,.react-datepicker__day--in-range,.react-datepicker__day--in-selecting-range{background:#16a34a;color:#fff}
                                        .react-datepicker__day--keyboard-selected{background:#22c55e}
                                        .react-datepicker__day--disabled{color:#475569}
                                    `}</style>
                                    <DatePicker
                                        selected={dateRange.from}
                                        onChange={([start, end]) => {
                                            setSelectedPreset('custom');
                                            setDateRange({ from: start, to: end });
                                        }}
                                        startDate={dateRange.from}
                                        endDate={dateRange.to}
                                        selectsRange inline locale="es" monthsShown={2}
                                    />
                                </div>
                                <div className="flex justify-end gap-2 mt-4">
                                    <DialogClose asChild>
                                        <Button variant="outline" className="border-white/10 text-white hover:bg-white/10">Cancelar</Button>
                                    </DialogClose>
                                    <DialogClose asChild>
                                        <Button className="bg-green-600 hover:bg-green-500 text-white font-bold" onClick={() => runReport()}>Generar</Button>
                                    </DialogClose>
                                </div>
                            </DialogContent>
                        </Dialog>

                        <Button className="shrink-0 bg-emerald-600 text-white hover:bg-emerald-500 px-4 sm:px-6" onClick={() => runReport()} disabled={loading}>
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            Generar
                        </Button>
                        </div>
                    </div>

                    {/* Branch filter */}
                    {branches.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                            <MapPin className="h-4 w-4 text-white/40 shrink-0" />
                            <Button
                                size="sm"
                                onClick={() => setSelectedBranch('ALL')}
                                className={cn('h-8 px-3 rounded-full text-xs font-medium transition-all',
                                    selectedBranch === 'ALL'
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white')}
                            >
                                Todas ({report?.orders?.length ?? 0})
                            </Button>
                            {branches.map(b => {
                                const count = report?.orders?.filter(o => o.branch === b).length ?? 0;
                                return (
                                    <Button
                                        key={b}
                                        size="sm"
                                        onClick={() => setSelectedBranch(b)}
                                        className={cn('h-8 px-3 rounded-full text-xs font-medium transition-all',
                                            selectedBranch === b
                                                ? 'bg-emerald-600 text-white'
                                                : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white')}
                                    >
                                        {b} ({count})
                                    </Button>
                                );
                            })}
                        </div>
                    )}

                    {error && (
                        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>
                    )}
                </CardHeader>
            </Card>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center gap-3 py-8 text-white/50">
                    <Loader2 className="h-5 w-5 animate-spin text-emerald-400" />
                    <span>Cargando datos desde DynamiaERP…</span>
                </div>
            )}

            {/* Metrics */}
            {!loading && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <MetricCard title="Ventas Netas"        value={formatCurrency(totalNetSales)} accent="text-green-400" />
                    <MetricCard title="Órdenes"             value={totalOrders.toLocaleString()} />
                    <MetricCard title="Impuestos"           value={formatCurrency(totalTaxes)} accent="text-blue-400" />
                    <MetricCard title="Comisiones Totales"  value={formatCurrency(totalCommissions)} accent="text-yellow-400" />
                    <MetricCard title="Mejor Vendedor"      value={bestSeller} />
                    <MetricCard title="Ticket Promedio"     value={totalOrders > 0 ? formatCurrency(totalNetSales / totalOrders) : '$0' } />
                    <MetricCard title="Sucursal Activa"     value={selectedBranch === 'ALL' ? 'Todas' : selectedBranch} />
                    <MetricCard title="Productos Distintos" value={productRows.length.toLocaleString()} />
                </div>
            )}

            {/* Tables */}
            {!loading && (
                <div className="grid gap-6 lg:grid-cols-2">
                    {/* Sellers */}
                    <Card className="border-white/10 bg-white/5 text-white min-w-0">
                        <CardHeader className="flex flex-row items-center justify-between gap-4">
                            <div>
                                <CardTitle className="text-base">Desglose por Vendedor</CardTitle>
                                <CardDescription className="text-white/60">
                                    {selectedBranch === 'ALL' ? 'Todas las sucursales' : selectedBranch} · comisiones configurables
                                </CardDescription>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" className="gap-2 bg-white/10 text-white hover:bg-white/20" onClick={() => setIsSellerChartOpen(true)}>
                                    <BarChart3 className="h-4 w-4" />
                                </Button>
                                <CommissionConfigDialog
                                    sellers={sellerRows.map(s => ({ key: s.key, name: s.name }))}
                                    commissionConfig={commissionConfig}
                                    onSave={handleCommissionSave}
                                />
                            </div>
                        </CardHeader>
                        <CardContent>
                            {sellerRows.length === 0 ? (
                                <p className="py-8 text-center text-sm text-white/50">Sin datos de vendedores para el período.</p>
                            ) : (
                                <div className="rounded-lg border border-white/10 overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="border-white/10 hover:bg-transparent">
                                                <TableHead className="text-white/70">Vendedor</TableHead>
                                                <TableHead className="text-right text-white/70">Ventas Netas</TableHead>
                                                <TableHead className="text-right text-white/70">Órdenes</TableHead>
                                                <TableHead className="text-right text-white/70">Comisión</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {sellerRows.map(s => (
                                                <TableRow key={s.key} className="border-white/5 hover:bg-white/5">
                                                    <TableCell className="font-medium text-white">{s.name}</TableCell>
                                                    <TableCell className="text-right text-green-400 font-semibold">{formatCurrency(s.netSales)}</TableCell>
                                                    <TableCell className="text-right text-white/70">{s.orders}</TableCell>
                                                    <TableCell className="text-right text-yellow-400">{formatCurrency(s.commission)}</TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow className="border-white/10 bg-white/5 font-bold">
                                                <TableCell className="text-white">TOTAL</TableCell>
                                                <TableCell className="text-right text-green-400">{formatCurrency(totalNetSales)}</TableCell>
                                                <TableCell className="text-right text-white">{totalOrders}</TableCell>
                                                <TableCell className="text-right text-yellow-400">{formatCurrency(totalCommissions)}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Products */}
                    <Card className="border-white/10 bg-white/5 text-white min-w-0">
                        <CardHeader className="flex flex-row items-center justify-between gap-4">
                            <div>
                                <CardTitle className="text-base">Ventas de Productos</CardTitle>
                                <CardDescription className="text-white/60">{productRows.length} productos · {selectedBranch === 'ALL' ? 'todas las sucursales' : selectedBranch}</CardDescription>
                            </div>
                            <div className="flex gap-2 items-center">
                                <Input
                                    placeholder="Buscar…"
                                    className="h-8 w-32 bg-white/5 text-white border-white/10 text-xs"
                                    value={productSearch}
                                    onChange={e => setProductSearch(e.target.value)}
                                />
                                <Button variant="outline" size="sm" className="gap-2 bg-white/10 text-white hover:bg-white/20" onClick={() => setIsProductChartOpen(true)}>
                                    <ShoppingBag className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {displayProductRows.length === 0 ? (
                                <p className="py-8 text-center text-sm text-white/50">
                                    {productSearch ? 'Sin resultados para esa búsqueda.' : 'Sin productos para el período.'}
                                </p>
                            ) : (
                                <div className="rounded-lg border border-white/10 overflow-x-auto max-h-[520px]">
                                    <Table>
                                        <TableHeader className="bg-white/5 sticky top-0 backdrop-blur-sm">
                                            <TableRow className="border-white/10 hover:bg-transparent">
                                                <TableHead className="text-white/70 cursor-pointer" onClick={() => handleSort('name')}>
                                                    Producto {productSort.key === 'name' && (productSort.direction === 'asc' ? '↑' : '↓')}
                                                </TableHead>
                                                <TableHead className="text-right text-white/70 cursor-pointer" onClick={() => handleSort('units')}>
                                                    Uds {productSort.key === 'units' && (productSort.direction === 'asc' ? '↑' : '↓')}
                                                </TableHead>
                                                <TableHead className="text-right text-white/70 cursor-pointer" onClick={() => handleSort('totalSales')}>
                                                    Ventas {productSort.key === 'totalSales' && (productSort.direction === 'asc' ? '↑' : '↓')}
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {displayProductRows.map(p => (
                                                <TableRow key={p.key} className="border-white/5 hover:bg-white/5">
                                                    <TableCell className="font-medium text-white text-xs">
                                                        <span className="truncate block max-w-[220px]" title={p.name}>{p.name}</span>
                                                    </TableCell>
                                                    <TableCell className="text-right text-white/70 text-xs">{p.units.toLocaleString()}</TableCell>
                                                    <TableCell className="text-right text-green-400 font-semibold text-xs">{formatCurrency(p.totalSales)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Chart Dialogs */}
            <ChartDialog open={isSummaryChartOpen} onOpenChange={setIsSummaryChartOpen} title="Ventas por día">
                <LineChart data={summaryChartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" stroke="#9ca3af" tick={{ fill: '#e5e7eb', fontSize: 12 }} tickLine={false} axisLine={false} dy={8} />
                    <YAxis yAxisId="left" stroke="#9ca3af" tickFormatter={v => formatCurrency(Number(v))} tick={{ fill: '#e5e7eb', fontSize: 12 }} tickLine={false} axisLine={false} />
                    <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" allowDecimals={false} tick={{ fill: '#e5e7eb', fontSize: 12 }} tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent className="border-white/10 bg-gray-900/95 text-white" />} />
                    <Line yAxisId="left"  type="monotone" dataKey="netSales" name="Ventas netas" stroke="var(--color-netSales)" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    <Line yAxisId="right" type="monotone" dataKey="orders"   name="Órdenes"     stroke="var(--color-orders)"   strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
            </ChartDialog>

            <ChartDialog open={isSellerChartOpen} onOpenChange={setIsSellerChartOpen} title="Rendimiento por Vendedor">
                <BarChart data={sellerChartData} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" stroke="#9ca3af" tick={{ fill: '#e5e7eb', fontSize: 12 }} tickLine={false} axisLine={false} />
                    <YAxis yAxisId="left" stroke="#9ca3af" tickFormatter={v => formatCurrency(Number(v))} tick={{ fill: '#e5e7eb', fontSize: 12 }} tickLine={false} axisLine={false} />
                    <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" allowDecimals={false} tick={{ fill: '#e5e7eb', fontSize: 12 }} tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent className="border-white/10 bg-gray-900/95 text-white" />} />
                    <Bar yAxisId="left"  dataKey="netSales" name="Ventas Netas" fill="hsl(199 82% 50%)" radius={[4,4,0,0]} barSize={36} />
                    <Bar yAxisId="right" dataKey="orders"   name="Órdenes"     fill="hsl(27 96% 61%)"  radius={[4,4,0,0]} barSize={36} />
                </BarChart>
            </ChartDialog>

            <ChartDialog open={isProductChartOpen} onOpenChange={setIsProductChartOpen} title="Top Productos por Unidades">
                <BarChart data={productChartData} layout="vertical" margin={{ left: 20, right: 30, top: 10, bottom: 10 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" stroke="#9ca3af" tick={{ fill: '#e5e7eb', fontSize: 12 }} tickLine={false} axisLine={false} />
                    <YAxis dataKey="name" type="category" stroke="#9ca3af" width={200} tick={{ fill: '#e5e7eb', fontSize: 11 }} tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent className="border-white/10 bg-gray-900/95 text-white" />} />
                    <Bar dataKey="units" name="Unidades" fill="hsl(262 83% 62%)" radius={[0,4,4,0]} barSize={20} />
                </BarChart>
            </ChartDialog>

            {/* Debug */}
            <Dialog open={isLogOpen} onOpenChange={setIsLogOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden border-white/10 bg-gray-950/95 text-white flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Datos Crudos del Reporte</DialogTitle>
                        <DialogDescription className="text-white/60">Inspección de la estructura recibida. Sucursal: {selectedBranch}</DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-auto rounded-md bg-black/50 p-4 font-mono text-xs">
                        <pre>{JSON.stringify({ selectedBranch, filteredOrders: filteredOrders.length, sellerRows, productRows: productRows.length }, null, 2)}</pre>
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function MetricCard({ title, value, accent = 'text-white' }) {
    return (
        <Card className="border-white/10 bg-white/5 text-white">
            <CardHeader className="space-y-1 pb-3">
                <CardDescription className="text-white/50 text-xs uppercase tracking-wide">{title}</CardDescription>
                <CardTitle className={`text-2xl font-bold ${accent}`}>{value}</CardTitle>
            </CardHeader>
        </Card>
    );
}

function ChartDialog({ open, onOpenChange, title, children }) {
    const config = {
        netSales:   { label: 'Ventas netas', color: 'hsl(142 70% 45%)' },
        orders:     { label: 'Órdenes',      color: 'hsl(208 80% 65%)' },
        commission: { label: 'Comisiones',   color: 'hsl(48 94% 58%)'  },
        units:      { label: 'Unidades',     color: 'hsl(262 83% 62%)' },
        totalSales: { label: 'Ventas',       color: 'hsl(13 88% 63%)'  },
    };
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl border-white/10 bg-gray-950/95 text-white">
                <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
                <div className="mt-4 h-[340px]">
                    <ChartContainer config={config} className="h-full">
                        <ResponsiveContainer>{children}</ResponsiveContainer>
                    </ChartContainer>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function CommissionConfigDialog({ sellers, commissionConfig, onSave }) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({});

    useEffect(() => {
        if (open) {
            setDraft(Object.fromEntries(Object.entries(commissionConfig).map(([k, v]) => [k, { ...v }])));
        }
    }, [open, commissionConfig]);

    const handleSave = async () => { await onSave(draft); setOpen(false); };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 bg-white/10 text-white hover:bg-white/20">
                    <Settings className="h-4 w-4" /> Comisiones
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg border-white/10 bg-gray-950/95 text-white">
                <DialogHeader>
                    <DialogTitle>Configurar Comisiones</DialogTitle>
                    <DialogDescription className="text-white/60">Porcentaje de comisión por vendedor</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-4 max-h-[400px] overflow-y-auto">
                    {sellers.map(seller => {
                        const cfg = draft[seller.key] ?? { enabled: true, percentage: 1 };
                        return (
                            <div key={seller.key} className="flex items-center gap-3 p-2 border-b border-white/10">
                                <Switch checked={cfg.enabled} onCheckedChange={() => setDraft(p => ({ ...p, [seller.key]: { ...p[seller.key], enabled: !p[seller.key]?.enabled } }))} />
                                <span className="flex-1 text-sm truncate">{seller.name}</span>
                                <div className="flex items-center gap-1 w-20">
                                    <Input
                                        type="number" min="0" step="0.1"
                                        className="h-7 bg-white/5 text-right text-sm"
                                        value={cfg.percentage}
                                        onChange={e => setDraft(p => ({ ...p, [seller.key]: { ...p[seller.key], percentage: parseFloat(e.target.value) || 0 } }))}
                                    />
                                    <span className="text-white/40 text-xs">%</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <DialogFooter>
                    <DialogClose asChild><Button variant="outline" className="border-white/10 text-white">Cancelar</Button></DialogClose>
                    <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-500 text-white">Guardar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
